import React, { useState } from 'react';
import { FiBookOpen, FiClock, FiEdit3, FiArrowRight } from 'react-icons/fi';
import { LANE_LIMIT, sumCounts } from './instructorHome';
import styles from './InstructorHome.module.css';

const ICONS = { qna: FiBookOpen, extensions: FiClock, grading: FiEdit3 };
const TONES = { courses: styles.toneQna, extensions: styles.toneExt, grading: styles.toneGrade };

/**
 * My courses, extension requests, and grading, side by side.
 * On phones the three counts become tiles and the chosen lane opens below.
 */
export default function Lanes({ lanes, coursesCard, courseCount, onApprove, onDecline, onGrade, onAllRequests, onGradingInbox }) {
  const defs = [
    {
      key: 'courses',
      title: 'My courses',
      short: 'My courses',
      rows: [],
      count: courseCount,
    },
    {
      key: 'extensions',
      title: 'Extension requests',
      short: 'Extensions',
      rows: lanes.extensions,
      count: lanes.extensions.length,
      empty: 'No requests waiting.',
      action: (row) => (
        <>
          <button type="button" className={`${styles.btn} ${styles.btnSm} ${styles.btnGo}`} onClick={() => onApprove(row.request)}>Approve</button>
          <button type="button" className={`${styles.btn} ${styles.btnSm}`} onClick={() => onDecline(row.request)}>Decline</button>
        </>
      ),
      foot: { label: 'All requests', onClick: onAllRequests },
    },
    {
      key: 'grading',
      title: 'To grade',
      short: 'To grade',
      rows: lanes.grading,
      count: sumCounts(lanes.grading),
      empty: 'All caught up.',
      action: (row) => (
        <button type="button" className={`${styles.btn} ${styles.btnSm} ${styles.btnGo}`} onClick={() => onGrade(row)}>
          Grade {row.count}
        </button>
      ),
      foot: { label: 'Grading inbox', onClick: onGradingInbox },
    },
  ];

  const firstBusy = defs.find((d) => d.count > 0)?.key || 'courses';
  const [picked, setPicked] = useState(null);
  const open = picked || firstBusy;

  const total = defs.filter((d) => d.key !== 'courses').reduce((n, d) => n + d.count, 0);

  return (
    <section className={styles.section} aria-labelledby="activity-title">
      <div className={styles.sectionHead}>
        <h2 id="activity-title" className={styles.sectionTitle}>My Courses &amp; Activity</h2>
        <span className={styles.pill}>{total}</span>
      </div>
      <div className={styles.tiles} role="tablist" aria-label="My Courses & Activity">
        {defs.map((d) => (
          <button
            key={d.key}
            type="button"
            role="tab"
            aria-selected={open === d.key}
            aria-controls={`lane-${d.key}`}
            className={`${styles.tile} ${TONES[d.key]} ${d.count ? '' : styles.zero} ${open === d.key ? styles.tileOn : ''}`}
            onClick={() => setPicked(d.key)}
          >
            <span className={styles.tileCount}>{d.count}</span>
            <span className={styles.tileLabel}>{d.short}</span>
          </button>
        ))}
      </div>

      <div className={styles.lanes}>
        {defs.map((d) => {
          if (d.key === 'courses') return (
            <div key={d.key} id="lane-courses" className={`${styles.lane} ${styles.coursesLane} ${open === d.key ? styles.laneOpen : ''}`}>
              {coursesCard}
            </div>
          );
          const Icon = ICONS[d.key];
          const shown = d.rows.slice(0, LANE_LIMIT);
          const more = d.rows.length - shown.length;
          return (
            <section
              key={d.key}
              id={`lane-${d.key}`}
              aria-labelledby={`lane-${d.key}-title`}
              className={`${styles.lane} ${TONES[d.key]} ${d.count ? '' : styles.zero} ${open === d.key ? styles.laneOpen : ''}`}
            >
              <div className={styles.laneHead}>
                <span className={styles.laneIcon} aria-hidden="true"><Icon size={17} /></span>
                <h3 id={`lane-${d.key}-title`} className={styles.laneTitle}>{d.title}</h3>
                <span className={styles.laneCount} aria-label={`${d.count} waiting`}>{d.count}</span>
              </div>
              {/* Fixed body: room for exactly LANE_LIMIT rows, so every lane is the same size. */}
              <div className={styles.laneBody}>
                {shown.length === 0 && <div className={styles.laneEmpty}>{d.empty}</div>}
                {shown.map((row) => (
                  <div key={row.id} className={styles.laneRow}>
                    <span className={styles.rowTitle} title={row.title}>{row.title}</span>
                    <span className={styles.rowMeta}>{row.meta}</span>
                    <div className={styles.rowActions}>{d.action(row)}</div>
                  </div>
                ))}
              </div>
              <div className={styles.laneFoot}>
                {d.foot.note ? (
                  <span className={styles.laneNote}>{d.foot.note}</span>
                ) : (
                  <button type="button" className={styles.linkBtn} onClick={d.foot.onClick}>
                    {d.foot.label} <FiArrowRight size={13} aria-hidden="true" />
                  </button>
                )}
                {more > 0 && <span className={styles.laneMore}>+{more} more</span>}
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
}
